/**
 * Stable per-browser device id for upstream login security checks.
 * Mirrors the tablet-app contract: UUID v4 in `X-Device-Fingerprint`.
 */

export const DEVICE_FINGERPRINT_STORAGE_KEY = 'nemolaunchpad.deviceFingerprint';
export const DEVICE_FINGERPRINT_HEADER = 'X-Device-Fingerprint';

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let cachedFingerprint = '';

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function createFingerprint(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last-resort fallback for exotic runtimes — still unique enough for a session.
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
}

function readStored(): string {
  if (!canUseStorage()) return '';
  try {
    const stored = localStorage.getItem(DEVICE_FINGERPRINT_STORAGE_KEY)?.trim() || '';
    return UUID_V4_PATTERN.test(stored) ? stored : '';
  } catch {
    return '';
  }
}

function writeStored(fingerprint: string): void {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(DEVICE_FINGERPRINT_STORAGE_KEY, fingerprint);
  } catch {
    // Private browsing / quota — keep the in-memory id for this session.
  }
}

/** Returns a stable UUID for this browser; minting one on first use. */
export function getDeviceFingerprint(): string {
  if (cachedFingerprint) return cachedFingerprint;

  const stored = readStored();
  if (stored) {
    cachedFingerprint = stored;
    return stored;
  }

  const fingerprint = createFingerprint();
  writeStored(fingerprint);
  cachedFingerprint = fingerprint;
  return fingerprint;
}

/** Test helper — clears the in-memory cache so a fresh browser can be simulated. */
export function resetDeviceFingerprintCache(): void {
  cachedFingerprint = '';
}
